import {
  pgTable,
  text,
  serial,
  integer,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  date,
  boolean,
  unique,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const cachedPlanRates = pgTable(
  "cached_plan_rates",
  {
    id: serial("id").primaryKey(),
    wordBrownPlanId: varchar("word_brown_plan_id", { length: 255 }).notNull(),
    carrierId: varchar("carrier_id", { length: 255 }),
    carrierName: varchar("carrier_name", { length: 255 }),
    planName: text("plan_name").notNull(),
    planCode: varchar("plan_code", { length: 100 }),
    coverageType: varchar("coverage_type", { length: 50 }).notNull(),
    metalTier: varchar("metal_tier", { length: 50 }),
    networkType: varchar("network_type", { length: 50 }),
    ratingAreaId: varchar("rating_area_id", { length: 50 }).notNull(),
    ratingAreaLabel: varchar("rating_area_label", { length: 100 }),
    effectiveMonth: date("effective_month").notNull(),
    rates: jsonb("rates").notNull().$type<Array<{
      age: number;
      rate: number;
      tobacco?: boolean;
    }>>(),
    benefits: jsonb("benefits").$type<Array<{
      name: string;
      value: string;
      category?: string;
    }>>(),
    planDetails: jsonb("plan_details").$type<{
      deductible?: string;
      oopMax?: string;
      officeVisit?: string;
      er?: string;
      rxTier1?: string;
      rxTier2?: string;
      [key: string]: any;
    }>(),
    rawApiResponse: jsonb("raw_api_response"),
    fetchedAt: timestamp("fetched_at").defaultNow().notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("cached_plan_rates_word_brown_id_idx").on(table.wordBrownPlanId),
    index("cached_plan_rates_carrier_idx").on(table.carrierName),
    index("cached_plan_rates_coverage_type_idx").on(table.coverageType),
    index("cached_plan_rates_rating_area_idx").on(table.ratingAreaId),
    index("cached_plan_rates_effective_month_idx").on(table.effectiveMonth),
    index("cached_plan_rates_plan_lookup_idx").on(
      table.wordBrownPlanId,
      table.ratingAreaId,
      table.effectiveMonth
    ),
    unique("cached_plan_rates_unique").on(
      table.wordBrownPlanId,
      table.ratingAreaId,
      table.effectiveMonth
    ),
  ]
);

export const planCacheSyncLogs = pgTable("plan_cache_sync_logs", {
  id: serial("id").primaryKey(),
  syncType: varchar("sync_type", { length: 50 }).notNull(),
  ratingAreaId: varchar("rating_area_id", { length: 50 }),
  coverageType: varchar("coverage_type", { length: 50 }),
  effectiveMonth: date("effective_month").notNull(),
  status: varchar("status", { length: 20 }).notNull(),
  plansProcessed: integer("plans_processed").default(0),
  ratesProcessed: integer("rates_processed").default(0),
  errors: jsonb("errors").$type<Array<{ planId: string; error: string }>>(),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  duration: integer("duration_ms"),
});

export const insertCachedPlanRateSchema = createInsertSchema(cachedPlanRates).omit({
  id: true,
  createdAt: true,
});

export const insertPlanCacheSyncLogSchema = createInsertSchema(planCacheSyncLogs).omit({
  id: true,
});

export type CachedPlanRate = typeof cachedPlanRates.$inferSelect;
export type InsertCachedPlanRate = z.infer<typeof insertCachedPlanRateSchema>;
export type PlanCacheSyncLog = typeof planCacheSyncLogs.$inferSelect;
export type InsertPlanCacheSyncLog = z.infer<typeof insertPlanCacheSyncLogSchema>;
